import { useState } from 'react';
import { Button, CircularProgress, Alert } from '@mui/material';
import { Sync } from '@mui/icons-material';

import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import Badge from '@/components/Badge';
import { CheckHQResponse, WorkersV2API } from '@/services/workersV2API';

type Props = {
  workerId: number;
  checkhq: CheckHQResponse;
  onSync?: () => void;
};

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <Stack
    style={{
      justifyContent: 'space-between',
      padding: '8px 0',
      borderBottom: '1px solid #F3F4F6',
    }}
  >
    <Small color="lighter">{label}</Small>
    <Body>{value}</Body>
  </Stack>
);

const CheckHQCard = ({ workerId, checkhq, onSync }: Props) => {
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const handleSync = async () => {
    try {
      setSyncing(true);
      setSyncResult(null);
      const result = await WorkersV2API.syncFromCheckHQ(workerId);
      if (result.success) {
        setSyncResult({
          success: true,
          message: 'Sync completed successfully',
        });
        onSync?.();
      } else {
        setSyncResult({
          success: false,
          message: result.error || 'Sync failed',
        });
      }
    } catch (err) {
      setSyncResult({ success: false, message: 'Failed to sync from CheckHQ' });
    } finally {
      setSyncing(false);
    }
  };

  if (!checkhq.available) {
    return (
      <Card title="CheckHQ Details">
        <Card.Section>
          <Stack vertical gap={8}>
            <Badge label="Not Available" status="neutral" />
            <Small color="lighter">
              {'reason' in checkhq && checkhq.reason
                ? checkhq.reason
                : 'error' in checkhq && checkhq.error
                  ? checkhq.error
                  : 'No CheckHQ data available for this worker'}
            </Small>
            {'employee_id' in checkhq && checkhq.employee_id && (
              <Small color="lighter">Employee ID: {checkhq.employee_id}</Small>
            )}
          </Stack>
        </Card.Section>
      </Card>
    );
  }

  const { data, employee_id, type } = checkhq;
  const onboard = data.onboard;

  return (
    <Card
      actions={[
        {
          label: syncing ? 'Syncing...' : 'Sync from CheckHQ',
          onAction: handleSync,
        },
      ]}
      title="CheckHQ Details"
    >
      {syncResult && (
        <Card.Section>
          <Alert
            severity={syncResult.success ? 'success' : 'error'}
            onClose={() => setSyncResult(null)}
          >
            {syncResult.message}
          </Alert>
        </Card.Section>
      )}

      <Card.Section>
        <Stack vertical gap={8}>
          <Body weight="semibold">Employee Info</Body>
          <Stack vertical gap={4}>
            <DetailRow label="Employee ID" value={employee_id} />
            <DetailRow
              label="Type"
              value={
                <Badge
                  label={type === 'employee' ? 'Employee' : 'Contractor'}
                  status={type === 'employee' ? 'theme' : 'neutral'}
                />
              }
            />
            <DetailRow
              label="Name"
              value={`${data.first_name} ${data.middle_name || ''} ${data.last_name}`.trim()}
            />
            <DetailRow label="Email" value={data.email} />
            <DetailRow label="DOB" value={data.dob} />
            <DetailRow
              label="SSN (Last 4)"
              value={data.ssn_last_four || 'N/A'}
            />
            <DetailRow
              label="SSN Validation"
              value={
                <Badge
                  label={data.ssn_validation_status || 'N/A'}
                  status={
                    data.ssn_validation_status === 'valid' ? 'theme' : 'warning'
                  }
                />
              }
            />
            <DetailRow
              label="Active"
              value={
                <Badge
                  label={data.active ? 'Yes' : 'No'}
                  status={data.active ? 'theme' : 'danger'}
                />
              }
            />
          </Stack>
        </Stack>
      </Card.Section>

      <Card.Section>
        <Stack vertical gap={8}>
          <Body weight="semibold">Onboarding Status</Body>
          <Stack vertical gap={4}>
            <DetailRow
              label="Status"
              value={
                <Badge
                  label={onboard.status}
                  status={
                    onboard.status === 'complete'
                      ? 'theme'
                      : onboard.status === 'blocking'
                        ? 'danger'
                        : 'warning'
                  }
                />
              }
            />
            {onboard.blocking_steps.length > 0 && (
              <DetailRow
                label="Blocking Steps"
                value={
                  <Stack
                    gap={4}
                    style={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}
                  >
                    {onboard.blocking_steps.map((step) => (
                      <Badge key={step} label={step} status="danger" />
                    ))}
                  </Stack>
                }
              />
            )}
            {onboard.remaining_steps.length > 0 && (
              <DetailRow
                label="Remaining Steps"
                value={
                  <Stack
                    gap={4}
                    style={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}
                  >
                    {onboard.remaining_steps.map((step) => (
                      <Badge key={step} label={step} status="warning" />
                    ))}
                  </Stack>
                }
              />
            )}
          </Stack>
        </Stack>
      </Card.Section>

      <Card.Section>
        <Stack vertical gap={8}>
          <Body weight="semibold">Payment</Body>
          <Stack vertical gap={4}>
            <DetailRow
              label="Payment Method"
              value={data.payment_method_preference || 'N/A'}
            />
            <DetailRow
              label="W2 Electronic Consent"
              value={
                <Badge
                  label={data.w2_electronic_consent_provided ? 'Yes' : 'No'}
                  status={
                    data.w2_electronic_consent_provided ? 'theme' : 'neutral'
                  }
                />
              }
            />
          </Stack>
        </Stack>
      </Card.Section>

      <Card.Section>
        <Stack vertical gap={8}>
          <Body weight="semibold">Residence</Body>
          <Stack vertical gap={4}>
            <DetailRow
              label="Address"
              value={`${data.residence.line1}${data.residence.line2 ? `, ${data.residence.line2}` : ''}`}
            />
            <DetailRow label="City" value={data.residence.city} />
            <DetailRow label="State" value={data.residence.state} />
            <DetailRow label="Postal Code" value={data.residence.postal_code} />
            <DetailRow label="Country" value={data.residence.country} />
          </Stack>
        </Stack>
      </Card.Section>

      <Card.Section>
        <Stack vertical gap={8}>
          <Body weight="semibold">Employment</Body>
          <Stack vertical gap={4}>
            <DetailRow label="Start Date" value={data.start_date || 'N/A'} />
            <DetailRow
              label="Termination Date"
              value={data.termination_date || 'N/A'}
            />
            <DetailRow label="Company" value={data.company} />
            <DetailRow
              label="Primary Workplace"
              value={data.primary_workplace || 'N/A'}
            />
          </Stack>
        </Stack>
      </Card.Section>
    </Card>
  );
};

export default CheckHQCard;
